import React, { useMemo } from 'react';
import { phasePaddingX } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { phasePaddingY } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { gapBetweenDemographics } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { DynamicPipelinePhaseResultLayout } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { DynamicPipelineAnalysisDemographicBreakdown } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysisDemographicBreakdown';
import { tryTranslateDynamic } from 'libs/end-to-end/src/phrase-utils';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';

export const DynamicPipelineAnalysisSection = (props: {
  phaseResult: DynamicPipelinePhaseResultLayout;
  nextPhaseResults: DynamicPipelinePhaseResultLayout[] | null;
  selectedLevel: string;
  dimension: string;
  computedGapBetweenPhases: number;
}) => {
  const {
    phaseResult,
    selectedLevel,
    dimension,
    nextPhaseResults,
    computedGapBetweenPhases,
  } = props;

  const language = useLanguage();
  const totalBase = useMemo(() => {
    const entries = Object.entries(phaseResult.demographic_data);
    return entries.reduce((acc, [, data]) => acc + data.count, 0);
  }, [phaseResult.demographic_data]);

  const showPercentage = true;

  return (
    <div className="flex flex-col flex-grow gap-5">
      <div
        className="flex flex-col flex-grow bg-white rounded border-[1px] border-gray-200"
        style={{
          gap: `${gapBetweenDemographics}px`,
          // Subtract 1px from padding to account for border
          padding: `${phasePaddingY - 1}px ${phasePaddingX - 1}px`,
        }}
      >
        <div className="flex flex-col gap-2 pt-1">
          <p className="font-sans text-base text-gray-800 font-bold text-ellipsis overflow-hidden whitespace-nowrap">
            {convertTranslatedContentToReactNode(
              tryTranslateDynamic({
                logger: uiAppLogger,
                phrase: phaseResult.name,
                language,
                customValue: phaseResult.customName ?? '',
              }),
            )}
          </p>
          <div className="flex flex-row gap-1 items-center">
            <p className="font-sans text-xs font-medium text-gray-800">
              {'Total: ' + totalBase.toString()}
            </p>
            {showPercentage && (
              <p className={'font-sans text-xs font-medium text-gray-600'}>
                {phaseResult.avg_pct_of_event.toFixed(2)}%
              </p>
            )}
          </div>
        </div>
        <div
          className="flex flex-col flex-grow gap-2"
          style={{
            minHeight: `${133 + phaseResult.demographic_data.length * 30}px`,
          }}
        >
          {phaseResult.demographic_data.map((data) => {
            return (
              <DynamicPipelineAnalysisDemographicBreakdown
                key={data.name}
                data={data}
                dimensionColumnHeaderValue={dimension}
                selectedLevel={selectedLevel}
                nextPhaseResults={nextPhaseResults}
                computedGapBetweenPhases={computedGapBetweenPhases}
                phaseResult={phaseResult}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
